import { useState, useCallback } from 'react';
import { Upload, Loader2 } from 'lucide-react';

interface DropZoneProps {
    onFileRead: (fileName: string, content: string) => void;
    accept?: string;
    className?: string;
}

export function DropZone({ onFileRead, accept = '.ts,.tsx,.js,.jsx,.py,.go,.rust', className = '' }: DropZoneProps) {
    const [isDragging, setIsDragging] = useState(false);
    const [isReading, setIsReading] = useState(false);

    const hasCustomPadding = className.includes('p-') || className.includes('px-') || className.includes('py-');

    const handleFile = useCallback((file: File) => {
        setIsReading(true);
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result as string;
            onFileRead(file.name, content);
            setIsReading(false);
        };
        reader.onerror = () => {
            console.error('Error reading file');
            setIsReading(false);
        };
        reader.readAsText(file);
    }, [onFileRead]);

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (file) {
            handleFile(file);
        }
    }, [handleFile]);

    const onDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const onDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFile(file);
        }
    };

    return (
        <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            className={`
                relative flex flex-col items-center justify-center 
                ${hasCustomPadding ? '' : 'p-6'} 
                border-2 border-dashed transition-all duration-200
                rounded-asymmetric-md cursor-pointer
                ${isDragging ? 'border-primary bg-primary/5' : 'border-border bg-surface/30'}
                ${isReading ? 'pointer-events-none opacity-60' : ''}
                ${className}
            `}
        >
            <input
                type="file"
                accept={accept}
                onChange={onSelectFile}
                className="absolute inset-0 opacity-0 cursor-pointer"
            />

            {isReading ? (
                <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm font-mono text-text-muted">Reading file...</p>
                </div>
            ) : (
                <div className="flex flex-col items-center text-center space-y-3">
                    <div className="p-3 bg-surface-hover rounded-full">
                        <Upload className={`h-6 w-6 ${isDragging ? 'text-primary' : 'text-text-muted'}`} />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-text-primary font-mono">
                            {isDragging ? 'Drop to upload' : 'Drag & drop your code file'}
                        </p>
                        <p className="text-xs text-text-muted font-mono mt-1">
                            Or click to browse (.ts, .py, .js, .go, .rust)
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
